'use client'

import ConfirmModal from '@/components/confirm-modal'
import HealthMapQuestionModal from '@/components/health-map/health-map-question-modal'
import HealthMapForm, {
    countAnswered,
    draftFromAnswers,
    draftToAnswers,
    HealthMapDraft,
} from '@/components/health-map/health-map-form'
import {
    createHealthMapShareLink,
    getHealthMapResponses,
    getHealthMapShareLinks,
    getHealthMapStructure,
    HealthMapCategory,
    HealthMapResponse,
    HealthMapShareLink,
    revokeHealthMapShareLink,
    saveHealthMapAnswers,
    sendHealthMap,
    startHealthMapResponse,
    submitHealthMapResponse,
} from '@/lib/api/health-map/health-map-api'
import { useProntuarioNavigate } from './prontuario-context'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdCheck,
    MdContentCopy,
    MdDelete,
    MdHistory,
    MdLink,
    MdMonitorHeart,
    MdSave,
    MdSend,
    MdSettings,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioData } from './prontuario-context'

/**
 * Mapa da Saúde — questionário geral do paciente (hábitos, sono, alimentação,
 * álcool, exercício, estresse), organizado em categorias e temas.
 *
 * Pode ser preenchido aqui, na consulta, ou pelo próprio paciente por um link
 * enviado no WhatsApp. As duas vias gravam o MESMO registro por rodada, e cada
 * preenchimento novo é um registro datado — é a comparação entre eles que mostra
 * a evolução.
 */
export default function HealthMapPage() {
    const { client, clientId } = useProntuarioData()
    const navigate = useProntuarioNavigate()
    const { user } = useAuth()

    const [categories, setCategories] = useState<HealthMapCategory[]>([])
    const [responses, setResponses] = useState<HealthMapResponse[]>([])
    const [links, setLinks] = useState<HealthMapShareLink[]>([])
    const [current, setCurrent] = useState<HealthMapResponse | null>(null)
    const [draft, setDraft] = useState<HealthMapDraft>({})
    const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null)

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [sending, setSending] = useState(false)
    const [generatingLink, setGeneratingLink] = useState(false)
    const [linkToRevoke, setLinkToRevoke] = useState<number | null>(null)
    const [showHistory, setShowHistory] = useState(false)
    const [showNewQuestion, setShowNewQuestion] = useState(false)

    useEffect(() => {
        if (clientId) loadAll()
    }, [clientId])

    const loadAll = async () => {
        if (!clientId) return
        setLoading(true)
        try {
            const id = parseInt(clientId, 10)
            const [structureRes, responsesRes, linksRes] = await Promise.all([
                getHealthMapStructure(),
                getHealthMapResponses(id),
                getHealthMapShareLinks(id),
            ])

            if (structureRes.success) {
                const list = structureRes.result || []
                setCategories(list)
                setActiveCategoryId(previous => previous ?? list[0]?.id ?? null)
            } else {
                toast.error(structureRes.error || 'Erro ao carregar o Mapa da Saúde')
            }

            const history = responsesRes.success ? responsesRes.result || [] : []
            setResponses(history)
            // Continua o rascunho em andamento; se não há, mostra o último enviado
            // (leitura), e o profissional decide quando começar outro.
            const draftResponse =
                history.find(item => item.status === 'DRAFT') ?? history[0] ?? null
            setCurrent(draftResponse)
            setDraft(draftFromAnswers(draftResponse?.answers ?? []))

            if (linksRes.success) setLinks(linksRes.result || [])
        } catch (error) {
            toast.error('Erro ao carregar o Mapa da Saúde')
        } finally {
            setLoading(false)
        }
    }

    /** Abre um preenchimento novo (ou devolve o rascunho já aberto). */
    const ensureResponse = async (): Promise<HealthMapResponse | null> => {
        if (current && current.status === 'DRAFT') return current
        if (!clientId) return null

        const response = await startHealthMapResponse(parseInt(clientId, 10), {
            professionalId: user?.professionalId ?? undefined,
        })
        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível iniciar o preenchimento')
            return null
        }
        setCurrent(response.result)
        setDraft(draftFromAnswers(response.result.answers))
        return response.result
    }

    const handleSave = async () => {
        const response = await ensureResponse()
        if (!response) return

        setSaving(true)
        try {
            const result = await saveHealthMapAnswers(
                response.id,
                draftToAnswers(draft)
            )
            if (result.success && result.result) {
                setCurrent(result.result)
                toast.success('Respostas salvas')
            } else {
                toast.error(result.error || 'Erro ao salvar respostas')
            }
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = async () => {
        const response = await ensureResponse()
        if (!response) return

        setSubmitting(true)
        try {
            // Salva antes de concluir: concluir sem gravar o que está na tela
            // perderia tudo o que foi digitado desde o último salvamento.
            const saved = await saveHealthMapAnswers(
                response.id,
                draftToAnswers(draft)
            )
            if (!saved.success) {
                toast.error(saved.error || 'Erro ao salvar respostas')
                return
            }
            const result = await submitHealthMapResponse(response.id)
            if (result.success) {
                toast.success('Mapa da Saúde concluído')
                await loadAll()
            } else {
                toast.error(result.error || 'Erro ao concluir')
            }
        } finally {
            setSubmitting(false)
        }
    }

    const handleStartNew = async () => {
        if (!clientId) return
        const response = await startHealthMapResponse(parseInt(clientId, 10), {
            professionalId: user?.professionalId ?? undefined,
        })
        if (response.success && response.result) {
            setCurrent(response.result)
            setDraft(draftFromAnswers(response.result.answers))
            setResponses(previous => [response.result!, ...previous])
            toast.success('Novo preenchimento iniciado')
        } else {
            toast.error(response.error || 'Não foi possível iniciar')
        }
    }

    const handleSend = async () => {
        if (!clientId) return
        setSending(true)
        try {
            const result = await sendHealthMap(parseInt(clientId, 10))
            if (result.success && result.result) {
                toast.success(result.result.message)
                await loadLinks()
            } else {
                toast.error(result.error || 'Erro ao enviar')
            }
        } finally {
            setSending(false)
        }
    }

    const handleGenerateLink = async () => {
        if (!clientId) return
        setGeneratingLink(true)
        try {
            const result = await createHealthMapShareLink(parseInt(clientId, 10))
            if (result.success && result.result) {
                await copyToClipboard(result.result.url)
                toast.success('Link gerado e copiado')
                await loadLinks()
            } else {
                toast.error(result.error || 'Erro ao gerar link')
            }
        } finally {
            setGeneratingLink(false)
        }
    }

    const loadLinks = async () => {
        if (!clientId) return
        const result = await getHealthMapShareLinks(parseInt(clientId, 10))
        if (result.success) setLinks(result.result || [])
    }

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text)
        } catch {
            // Navegador sem permissão de área de transferência: o link continua
            // visível na lista para copiar à mão.
        }
    }

    const confirmRevoke = async () => {
        if (linkToRevoke == null) return
        const result = await revokeHealthMapShareLink(linkToRevoke)
        if (result.success) {
            toast.success('Link revogado')
            await loadLinks()
        } else {
            toast.error(result.error || 'Erro ao revogar link')
        }
        setLinkToRevoke(null)
    }

    const activeCategory =
        categories.find(category => category.id === activeCategoryId) ?? null
    /**
     * Enviar link de um questionário vazio leva o paciente a uma tela em branco —
     * e a mensagem já não pode ser desfeita. O backend também recusa; aqui a ação
     * nem fica clicável, e o título diz o porquê.
     */
    const questionCount = categories.reduce(
        (total, category) =>
            total +
            category.themes.reduce(
                (themeTotal, theme) => themeTotal + theme.questions.length,
                0
            ),
        0
    )
    const questionnaireEmpty = questionCount === 0
    const progress = countAnswered(categories, draft)
    const readOnly = current?.status === 'COMPLETED'

    const linkStatus = (link: HealthMapShareLink): string => {
        if (link.revokedAt) return 'Revogado'
        if (link.usedAt) return 'Respondido'
        if (new Date(link.expiresAt).getTime() < Date.now()) return 'Expirado'
        if (link.openedAt) return 'Aberto pelo paciente'
        return 'Aguardando resposta'
    }

    if (loading) {
        return (
            <div className="flex min-h-[300px] flex-col items-center justify-center gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-sm text-base-content/60">
                    Carregando o Mapa da Saúde...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Cabeçalho */}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-xl font-bold text-base-content">
                        Mapa da Saúde
                    </h2>
                    <p className="text-sm text-base-content/60">
                        Hábitos, rotina e bem-estar do paciente
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-outline btn-sm gap-2"
                        onClick={handleSend}
                        disabled={sending || questionnaireEmpty}
                        title={
                            questionnaireEmpty
                                ? 'Cadastre ao menos uma pergunta antes de enviar'
                                : 'Enviar o questionário ao paciente por WhatsApp e e-mail'
                        }
                    >
                        {sending ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <MdSend className="text-base" />
                        )}
                        Enviar ao paciente
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline btn-sm gap-2"
                        onClick={handleGenerateLink}
                        disabled={generatingLink || questionnaireEmpty}
                        title={
                            questionnaireEmpty
                                ? 'Cadastre ao menos uma pergunta antes de gerar o link'
                                : 'Gerar link para o paciente responder'
                        }
                    >
                        {generatingLink ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <MdLink className="text-base" />
                        )}
                        Gerar link
                    </button>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={() => setShowHistory(previous => !previous)}
                    >
                        <MdHistory className="text-base" />
                        Histórico ({responses.length})
                    </button>
                    {/* A pergunta que falta é descoberta respondendo, não montando o
                        questionário — por isso ela nasce aqui também. */}
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={() => setShowNewQuestion(true)}
                        disabled={categories.length === 0}
                    >
                        <MdAdd className="text-base" />
                        Nova pergunta
                    </button>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-square"
                        title="Configurar o questionário"
                        onClick={() => navigate('/medical/health-map/settings')}
                    >
                        <MdSettings className="text-base" />
                    </button>
                </div>
            </div>

            {/* Preenchimento atual */}
            {current && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-base-300/50 bg-base-100 p-4">
                    <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-secondary/10 p-2">
                            <MdMonitorHeart className="text-xl text-secondary" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-base-content">
                                {current.status === 'COMPLETED'
                                    ? `Concluído em ${format(
                                          new Date(
                                              current.submittedAt ||
                                                  current.updatedAt
                                          ),
                                          "dd/MM/yyyy 'às' HH:mm",
                                          { locale: ptBR }
                                      )}`
                                    : 'Preenchimento em andamento'}
                            </p>
                            <p className="text-xs text-base-content/60">
                                {progress.answered} de {progress.total} perguntas
                                respondidas
                                {current.filledBy === 'PATIENT'
                                    ? ' — respondido pelo paciente'
                                    : ''}
                            </p>
                        </div>
                    </div>
                    {readOnly ? (
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm gap-2"
                            onClick={handleStartNew}
                        >
                            Novo preenchimento
                        </button>
                    ) : (
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm gap-2"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <MdSave className="text-base" />
                                )}
                                Salvar
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm gap-2"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <MdCheck className="text-base" />
                                )}
                                Concluir
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Histórico */}
            {showHistory && (
                <div className="rounded-xl border border-base-300/50 bg-base-100 p-4">
                    <h3 className="mb-3 text-sm font-semibold">
                        Preenchimentos anteriores
                    </h3>
                    {responses.length === 0 ? (
                        <p className="text-sm text-base-content/60">
                            Nenhum preenchimento registrado.
                        </p>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {responses.map(response => (
                                <button
                                    key={response.id}
                                    type="button"
                                    onClick={() => {
                                        setCurrent(response)
                                        setDraft(
                                            draftFromAnswers(response.answers)
                                        )
                                        setShowHistory(false)
                                    }}
                                    className={`flex items-center justify-between rounded-lg border p-3 text-left transition-colors ${
                                        current?.id === response.id
                                            ? 'border-secondary bg-secondary/10'
                                            : 'border-base-300/50 hover:bg-base-200/40'
                                    }`}
                                >
                                    <span className="text-sm">
                                        {format(
                                            new Date(
                                                response.submittedAt ||
                                                    response.createdAt
                                            ),
                                            "dd/MM/yyyy 'às' HH:mm",
                                            { locale: ptBR }
                                        )}
                                        <span className="ml-2 text-xs text-base-content/50">
                                            {response.filledBy === 'PATIENT'
                                                ? 'pelo paciente'
                                                : response.professional?.name ||
                                                  'pela equipe'}
                                        </span>
                                    </span>
                                    <span
                                        className={`badge badge-sm ${
                                            response.status === 'COMPLETED'
                                                ? 'badge-success text-white'
                                                : 'badge-warning'
                                        }`}
                                    >
                                        {response.status === 'COMPLETED'
                                            ? 'Concluído'
                                            : 'Em andamento'}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Links gerados */}
            {links.length > 0 && (
                <div className="rounded-xl border border-base-300/50 bg-base-100 p-4">
                    <h3 className="mb-3 text-sm font-semibold">
                        Links enviados ao paciente
                    </h3>
                    <div className="flex flex-col gap-2">
                        {links.map(link => (
                            <div
                                key={link.id}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-base-300/50 p-3"
                            >
                                <div className="min-w-0">
                                    <p className="truncate text-sm">
                                        {link.url}
                                    </p>
                                    <p className="text-xs text-base-content/50">
                                        {linkStatus(link)} · válido até{' '}
                                        {format(
                                            new Date(link.expiresAt),
                                            'dd/MM/yyyy',
                                            { locale: ptBR }
                                        )}
                                        {link.createdBy
                                            ? ` · por ${link.createdBy.name}`
                                            : ''}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm btn-square"
                                        title="Copiar link"
                                        onClick={() => {
                                            copyToClipboard(link.url)
                                            toast.success('Link copiado')
                                        }}
                                    >
                                        <MdContentCopy className="text-base" />
                                    </button>
                                    {!link.revokedAt && !link.usedAt && (
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-sm btn-square text-error"
                                            title="Revogar link"
                                            onClick={() =>
                                                setLinkToRevoke(link.id)
                                            }
                                        >
                                            <MdDelete className="text-base" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Categoria existe, pergunta não: o caso que deixava o envio liberado. */}
            {categories.length > 0 && questionnaireEmpty && (
                <div className="alert alert-warning">
                    <span>
                        Nenhuma pergunta cadastrada ainda. Monte o questionário em
                        Configurações antes de enviar ao paciente.
                    </span>
                </div>
            )}

            {/* Categorias (subabas) + perguntas */}
            {categories.length === 0 ? (
                <div className="rounded-xl border border-base-300/50 bg-base-100 p-8 text-center">
                    <MdMonitorHeart className="mx-auto mb-3 text-4xl text-base-content/20" />
                    <p className="text-base-content/60">
                        Nenhuma categoria cadastrada no Mapa da Saúde.
                    </p>
                    <p className="mt-1 text-sm text-base-content/50">
                        Monte o questionário em Configurações para começar.
                    </p>
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto rounded-xl border border-base-300/50 bg-base-100 px-2 py-2">
                        <div className="tabs tabs-bordered w-max flex-nowrap">
                            {categories.map(category => (
                                <button
                                    key={category.id}
                                    type="button"
                                    onClick={() =>
                                        setActiveCategoryId(category.id)
                                    }
                                    className={`tab gap-2 whitespace-nowrap ${
                                        activeCategoryId === category.id
                                            ? 'tab-active font-semibold text-secondary'
                                            : 'text-base-content/70 hover:text-base-content'
                                    }`}
                                >
                                    {category.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {activeCategory && (
                        <HealthMapForm
                            category={activeCategory}
                            draft={draft}
                            onChange={setDraft}
                            disabled={readOnly}
                        />
                    )}
                </>
            )}

            {showNewQuestion && (
                <HealthMapQuestionModal
                    themes={categories.flatMap(category =>
                        category.themes.map(theme => ({
                            id: theme.id,
                            name: theme.name,
                            categoryName: category.name,
                        }))
                    )}
                    onClose={() => setShowNewQuestion(false)}
                    onSaved={() => loadAll()}
                />
            )}

            <ConfirmModal
                isOpen={linkToRevoke !== null}
                onClose={() => setLinkToRevoke(null)}
                onConfirm={confirmRevoke}
                title="Revogar link"
                message={`O paciente ${
                    client?.name || ''
                } não conseguirá mais abrir este link. Os links já respondidos não são afetados.`}
                confirmButtonText="Revogar"
            />
        </div>
    )
}
